import { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  User,
  Mail,
  Phone,
  Star,
  MoreHorizontal,
  Edit2,
  Eye,
  TrendingUp,
  Calendar,
  Gift,
  Crown,
  Loader2,
  LayoutGrid,
  List,
  Trash2,
  ShoppingBag,
  Wallet,
  Users,
  UserPlus,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';
import { useCustomers, type Customer } from '@/hooks/useCustomers';
import { AddCustomerFormModal } from '@/components/AddCustomerFormModal';
import { toast } from 'sonner';
import { CustomerAccountsTab } from '@/components/customers/CustomerAccountsTab';
import { CreateCustomerAccountDialog } from '@/components/customers/CreateCustomerAccountDialog';
import { LocationSelector } from '@/components/location/LocationSelector';

const tierColors: Record<string, string> = {
  bronze: 'bg-amber-700/10 text-amber-700',
  silver: 'bg-gray-400/10 text-gray-500',
  gold: 'bg-yellow-500/10 text-yellow-600',
  platinum: 'bg-purple-500/10 text-purple-600',
};

type ViewMode = 'list' | 'card';
type TabValue = 'customers' | 'accounts';

export default function Customers() {
  const [activeTab, setActiveTab] = useState<TabValue>('customers');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<string | null>(null);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showCreateAccountDialog, setShowCreateAccountDialog] = useState(false);
  const [preSelectedCustomer, setPreSelectedCustomer] = useState<Customer | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { formatCurrency } = useCurrency();
  const { customers, isLoading } = useCustomers(debouncedSearch);

  const handleCreateKioskAccount = (customer: Customer) => {
    setPreSelectedCustomer(customer);
    setShowCreateAccountDialog(true);
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredCustomers = customers.filter((customer) => {
    if (tierFilter && customer.loyalty_tier !== tierFilter) return false;
    return true;
  });

  const stats = {
    total: customers.length,
    vip: customers.filter((c) => c.loyalty_tier === 'platinum' || c.loyalty_tier === 'gold').length,
    totalSpent: customers.reduce((sum, c) => sum + (c.total_spent || 0), 0),
    avgSpent: customers.length > 0
      ? customers.reduce((sum, c) => sum + (c.total_spent || 0), 0) / customers.length
      : 0,
  };

  const handleViewCustomer = (_customerId: string) => {
    toast.success('View Customer', {
        description: 'Customer details view coming soon',
      });
  };

  const handleEditCustomer = (_customerId: string) => {
    toast.success('Edit Customer', {
        description: 'Customer edit functionality coming soon',
      });
  };

  const handleViewOrders = (_customerId: string) => {
    toast.success('View Orders', {
        description: 'Customer orders view coming soon',
      });
  };

  const handleDeleteCustomer = (_customerId: string, customerName: string) => {
    if (confirm(`Are you sure you want to delete ${customerName}?`)) {
      toast.success('Delete Customer', {
        description: 'Customer delete functionality coming soon',
      });
    }
  };

  const header = (
    <div className="flex items-center justify-between gap-4">
      <div>
        <h1 className="text-2xl font-display font-semibold tracking-tight">
          Customers
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage your customer database, accounts, and wallets
        </p>
      </div>
      <div className="flex items-center gap-3">
        {activeTab === 'customers' && (
          <Button variant="blaze" className="gap-2" onClick={() => setShowAddCustomerModal(true)}>
            <Plus className="h-4 w-4" />
            Add Customer
          </Button>
        )}
        <LocationSelector variant="filter" showAllOption={false} />
      </div>
    </div>
  );

  return (
    <DashboardLayout header={header}>
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)} className="mb-6">
        <TabsList>
          <TabsTrigger value="customers" className="gap-2">
            <Users className="h-4 w-4" />
            Customers
          </TabsTrigger>
          <TabsTrigger value="accounts" className="gap-2">
            <Wallet className="h-4 w-4" />
            Customer Accounts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="accounts" className="mt-6">
          <CustomerAccountsTab />
        </TabsContent>

        <TabsContent value="customers" className="mt-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-primary/10">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total Customers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-purple-500/10">
                <Crown className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{stats.vip}</p>
                <p className="text-xs text-muted-foreground">VIP Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-green-500/10">
                <TrendingUp className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{formatCurrency(stats.totalSpent)}</p>
                <p className="text-xs text-muted-foreground">Total Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-blue-500/10">
                <Gift className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{formatCurrency(stats.avgSpent)}</p>
                <p className="text-xs text-muted-foreground">Avg. Customer Value</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={tierFilter === null ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setTierFilter(null)}
          >
            All Tiers
          </Button>
          {['bronze', 'silver', 'gold', 'platinum'].map((tier) => (
            <Button
              key={tier}
              variant={tierFilter === tier ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setTierFilter(tier)}
              className="capitalize"
            >
              {tier}
            </Button>
          ))}
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-1 ml-auto border rounded-lg p-1">
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'card' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8"
            onClick={() => setViewMode('card')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* List View */}
      {!isLoading && viewMode === 'list' && filteredCustomers.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left p-3 font-medium text-sm">Customer</th>
                  <th className="text-left p-3 font-medium text-sm">Contact</th>
                  <th className="text-left p-3 font-medium text-sm">Tier</th>
                  <th className="text-left p-3 font-medium text-sm">Points</th>
                  <th className="text-left p-3 font-medium text-sm">Total Spent</th>
                  <th className="text-left p-3 font-medium text-sm">Visits</th>
                  <th className="text-left p-3 font-medium text-sm">Last Visit</th>
                  <th className="text-right p-3 font-medium text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer) => {
                  const tier = customer.loyalty_tier || 'bronze';
                  const isVip = tier === 'platinum' || tier === 'gold';
                  const customerName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();

                  return (
                    <tr key={customer.id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                              {customer.first_name?.[0] || '?'}
                              {customer.last_name?.[0] || ''}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{customerName || 'Unknown'}</p>
                              {isVip && (
                                <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="space-y-1">
                          {customer.email && (
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              <span className="truncate max-w-[180px]">{customer.email}</span>
                            </div>
                          )}
                          {customer.phone && (
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              <Phone className="h-3 w-3" />
                              <span>{customer.phone}</span>
                            </div>
                          )}
                          {!customer.email && !customer.phone && (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge className={cn('capitalize text-xs', tierColors[tier] || tierColors.bronze)}>
                          {tier}
                        </Badge>
                      </td>
                      <td className="p-3 text-sm">
                        {(customer.loyalty_points || 0).toLocaleString()}
                      </td>
                      <td className="p-3 text-sm font-medium">
                        {formatCurrency(customer.total_spent || 0)}
                      </td>
                      <td className="p-3 text-sm">
                        {customer.total_visits || 0}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {customer.last_visit_at
                          ? new Date(customer.last_visit_at).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleViewCustomer(customer.id)}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEditCustomer(customer.id)}>
                                <Edit2 className="h-4 w-4 mr-2" />
                                Edit Customer
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleViewOrders(customer.id)}>
                                <ShoppingBag className="h-4 w-4 mr-2" />
                                View Orders
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleCreateKioskAccount(customer)}>
                                <UserPlus className="h-4 w-4 mr-2" />
                                Create Kiosk Account
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => handleDeleteCustomer(customer.id, customerName)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Customer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Card View */}
      {!isLoading && viewMode === 'card' && filteredCustomers.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCustomers.map((customer) => {
            const tier = customer.loyalty_tier || 'bronze';
            const isVip = tier === 'platinum' || tier === 'gold';
            const customerName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();

            return (
              <Card key={customer.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                        {customer.first_name?.[0] || '?'}
                        {customer.last_name?.[0] || ''}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold truncate">
                          {customer.first_name} {customer.last_name}
                        </h3>
                        {isVip && (
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={cn('capitalize text-xs', tierColors[tier] || tierColors.bronze)}>
                          {tier}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {(customer.loyalty_points || 0).toLocaleString()} pts
                        </span>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleViewCustomer(customer.id)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEditCustomer(customer.id)}>
                          <Edit2 className="h-4 w-4 mr-2" />
                          Edit Customer
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleViewOrders(customer.id)}>
                          <ShoppingBag className="h-4 w-4 mr-2" />
                          View Orders
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleCreateKioskAccount(customer)}>
                          <UserPlus className="h-4 w-4 mr-2" />
                          Create Kiosk Account
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => handleDeleteCustomer(customer.id, customerName)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Customer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="mt-4 space-y-2">
                    {customer.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="truncate text-muted-foreground">{customer.email}</span>
                      </div>
                    )}
                    {customer.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-muted-foreground">{customer.phone}</span>
                      </div>
                    )}
                    {customer.last_visit_at && (
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-muted-foreground">
                          Last visit: {new Date(customer.last_visit_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div>
                      <p className="text-xs text-muted-foreground">Total Spent</p>
                      <p className="font-semibold">{formatCurrency(customer.total_spent || 0)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Visits</p>
                      <p className="font-semibold">{customer.total_visits || 0}</p>
                    </div>
                  </div>

                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredCustomers.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <User className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-medium">No customers found</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            {searchQuery ? 'Try a different search term' : 'Start by adding your first customer'}
          </p>
          <Button variant="blaze" className="gap-2" onClick={() => setShowAddCustomerModal(true)}>
            <Plus className="h-4 w-4" />
            Add Customer
          </Button>
        </div>
      )}

        </TabsContent>
      </Tabs>

      {/* Add Customer Modal */}
      <AddCustomerFormModal
        open={showAddCustomerModal}
        onOpenChange={setShowAddCustomerModal}
      />

      {/* Create Kiosk Account Dialog */}
      <CreateCustomerAccountDialog
        open={showCreateAccountDialog}
        onOpenChange={(open) => {
          setShowCreateAccountDialog(open);
          if (!open) setPreSelectedCustomer(null);
        }}
        preSelectedCustomer={preSelectedCustomer}
      />
    </DashboardLayout>
  );
}
